import { FC, useCallback } from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { InfoOutlined, Link } from '@mui/icons-material';
import { Box, Button, InputAdornment, Typography } from '@mui/material';

import DeactivatedSupplementMessage from '@components/DeactivatedSupplementMessage/DeactivatedSupplementMessage';
import FileUploadErrors from '@components/FormFileUploadErrors/FormFileUploadErrors';
import FileUploadField from '@components/FormFileUploadField/FileUploadField';
import FormSelect from '@components/FormSelect/FormSelect';
import FormTextField from '@components/FormTextField/FormTextField';
import RichContent from '@components/RichContent/RichContent';
import {
  fileUploadErrorsSelector,
  fileUploadProgressSelector,
} from '@store/features/fileUploadState/selectors';
import useSupplementCategories from '@store/hooks/useSupplementCategories';
import { getParsedText } from '@views/AdminInquiriesPage/AdminInquiryList/SupplementInquiryDetails/SupplementInformationForm/utils';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import { ISupplementNeedsInfoFormProps } from './SupplementNeedsInfoForm.interface';

import './style.scss';

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 576,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 767,
    settings: {
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 7,
    },
  },
  {
    breakpoint: 10000,
    settings: {
      slidesToShow: 1,
    },
  },
];

const SupplementNeedsInfoForm: FC<ISupplementNeedsInfoFormProps> = ({
  adminComments,
  handleSubmit,
  hasUnseenResponses,
  hasValidationErrors,
  isDeactivated,
  isLastOneThatNeedsInfo,
  onClose,
  submitting,
}) => {
  const { t } = useTranslation();

  const fileUploadErrors = useSelector(fileUploadErrorsSelector);

  const supplementCategories = useSupplementCategories();
  const submitLabel = isLastOneThatNeedsInfo
    ? t('inquiryResponse.resendInquiry')
    : t('common.save');

  const uploadingFilesInProgress = useSelector(fileUploadProgressSelector);

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const submitDisabled = hasValidationErrors || submitting || uploadingFilesInProgress;

  const handleSubmitClick = useCallback(
    (event: React.BaseSyntheticEvent) => {
      if (submitDisabled || !handleSubmit) {
        return;
      }
      handleSubmit(event);
    },
    [handleSubmit, submitDisabled],
  );

  return (
    <form className="supplement-needs-info-form" onSubmit={handleSubmitClick} noValidate>
      <Typography variant="h3" className="supplement-needs-info-form__title">
        {t('inquiryResponse.needsInfoTitle')}
      </Typography>

      {isDeactivated && (
        <DeactivatedSupplementMessage className="supplement-needs-info-form__deactivated" />
      )}

      <p className="supplement-needs-info-form__required small">{t('common.required')}</p>

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input"
        label={t('addSupplementModal.supplementName')}
        name="supplementName"
        maxRows={2}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input"
        label={t('addSupplementModal.companyName')}
        name="companyName"
        format={removeWhiteSpaces}
        formatOnBlur
        required
      />

      <Field
        component={FormSelect}
        className="supplement-needs-info-form__input"
        label={t('addSupplementModal.productCategory')}
        name="supplementCategoryId"
        options={supplementCategories}
      />

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input supplement-needs-info-form__link"
        label={t('addSupplementModal.linkToProductPage')}
        name="linkToProductPage"
        InputProps={{
          startAdornment: (
            <InputAdornment className="supplement-needs-info-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        format={removeWhiteSpaces}
        formatOnBlur
        required
      />

      <div className="supplement-needs-info-form__admin-comments">
        {adminComments ? <RichContent>{getParsedText(adminComments)}</RichContent> : '-'}
      </div>

      <Field
        component={FormTextField}
        className="supplement-needs-info-form__input supplement-needs-info-form__note"
        label={t('inquiryResponse.needsInfoAddNote')}
        name="note"
        InputProps={{
          startAdornment: (
            <InputAdornment className="supplement-needs-info-form__input-icon" position="start">
              <img src="/images/img-note.svg" />
            </InputAdornment>
          ),
        }}
        rows={3}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        name="images"
        component={FileUploadField}
        responsiveImagesSettings={uploadImagesSliderResponsiveSettings}
        slidesToShow={1}
        className="supplement-needs-info-form__input"
        InputProps={{
          startAdornment: (
            <InputAdornment className="user-entered-supplement-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        formatOnBlur
      />

      {!!fileUploadErrors.length && (
        <Box className="supplement-needs-info-form__file-errors">
          <FileUploadErrors />
        </Box>
      )}

      {isLastOneThatNeedsInfo && hasUnseenResponses && (
        <p className="supplement-needs-info-form__has-unseen-responses">
          <InfoOutlined />
          {t('inquiryResponse.hasUnseenResponses')}
        </p>
      )}

      <div className="supplement-needs-info-form__controls">
        <Button
          className="supplement-needs-info-form__submit"
          type="submit"
          variant="contained"
          disabled={submitDisabled}
        >
          <img src="/images/img-submit.svg" />
          &nbsp;&nbsp;{submitLabel}
        </Button>

        <Button className="supplement-needs-info-form__cancel" variant="text" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
};

export default SupplementNeedsInfoForm;
